///
/// EarthBrowser Admin Dashboard - Feature Flags API
///
/// Created: 2025-11-27
/// Copyright (c) 2025 Geodata Labs
///

import { authenticatedApi } from "./authenticatedApi"
import type {
  FeatureFlag,
  FeatureFlagCreateRequest,
  FeatureFlagUpdateRequest,
  FeatureFlagToggleRequest,
  FeatureFlagsListResponse,
  FeatureFlagHistoryResponse,
  FeatureFlagCategory,
} from "@shared-types"

export const featureFlagsApi = authenticatedApi.injectEndpoints({
  endpoints: (builder) => ({
    getFeatureFlags: builder.query<FeatureFlagsListResponse, void>({
      query: () => "/api/admin/features",
      providesTags: ["FeatureFlags"],
    }),

    getFeatureFlagById: builder.query<FeatureFlag, number>({
      query: (id) => `/api/admin/features/${id}`,
      providesTags: (_result, _error, id) => [{ type: "FeatureFlags", id }],
    }),

    createFeatureFlag: builder.mutation<FeatureFlag, FeatureFlagCreateRequest>({
      query: (body) => ({
        url: "/api/admin/features",
        method: "POST",
        body,
      }),
      invalidatesTags: ["FeatureFlags"],
    }),

    updateFeatureFlag: builder.mutation<FeatureFlag, { id: number; body: FeatureFlagUpdateRequest }>({
      query: ({ id, body }) => ({
        url: `/api/admin/features/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "FeatureFlags",
        { type: "FeatureFlags", id },
      ],
    }),

    toggleFeatureFlag: builder.mutation<FeatureFlag, { id: number; body: FeatureFlagToggleRequest }>({
      query: ({ id, body }) => ({
        url: `/api/admin/features/${id}/toggle`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "FeatureFlags",
        { type: "FeatureFlags", id },
      ],
    }),

    deleteFeatureFlag: builder.mutation<{ success: boolean; message: string }, number>({
      query: (id) => ({
        url: `/api/admin/features/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["FeatureFlags"],
    }),

    getFeatureFlagHistory: builder.query<
      FeatureFlagHistoryResponse,
      { id: number; page?: number; limit?: number }
    >({
      query: ({ id, page = 1, limit = 50 }) => {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
        })
        return `/api/admin/features/${id}/history?${params}`
      },
      providesTags: (_result, _error, { id }) => [{ type: "FeatureFlags", id }],
    }),
  }),
})

export const {
  useGetFeatureFlagsQuery,
  useGetFeatureFlagByIdQuery,
  useCreateFeatureFlagMutation,
  useUpdateFeatureFlagMutation,
  useToggleFeatureFlagMutation,
  useDeleteFeatureFlagMutation,
  useGetFeatureFlagHistoryQuery,
} = featureFlagsApi

export type { FeatureFlag, FeatureFlagCategory }

